function accessoryFinderDebounce(fn, wait = 250) {
  let timeoutId;

  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), wait);
  };
}

function normalizeSearchTerm(query) {
  const normalizedQuery = (query || '').trim();

  if (
    normalizedQuery.startsWith('sku:"') &&
    normalizedQuery.endsWith('"') &&
    normalizedQuery.length > 'sku:""'.length
  ) {
    return normalizedQuery.slice(5, -1);
  }

  return normalizedQuery;
}

function isSkuSearchCandidate(query) {
  if (!query || query.includes(':') || query.includes('"') || query.includes('(') || query.includes(')')) {
    return false;
  }

  if (query.includes(' ')) return false;
  if (query.length < 3) return false;
  if (!/^[A-Za-z0-9._/-]+$/.test(query)) return false;

  return /\d/.test(query) || /[-_/.]/.test(query);
}

function buildSkuSearchQueries(query) {
  const normalizedQuery = normalizeSearchTerm(query);
  const queries = [normalizedQuery];

  if (normalizedQuery.toUpperCase().startsWith('KRC-')) {
    const withoutPrefix = normalizedQuery.slice(4);
    if (withoutPrefix) queries.push(withoutPrefix);
  } else {
    queries.push(`KRC-${normalizedQuery}`);
  }

  return [...new Set(queries)];
}

function normalizeComparableValue(value) {
  return normalizeSearchTerm(value || '')
    .toLocaleLowerCase('tr')
    .replace(/[\s._/-]+/g, '');
}

function buildPredictiveSearchQueries(query) {
  const normalizedQuery = normalizeSearchTerm(query);
  const queries = [normalizedQuery];

  if (isSkuSearchCandidate(normalizedQuery)) {
    buildSkuSearchQueries(normalizedQuery).forEach((candidate) => queries.push(candidate));

    const compactQuery = normalizeComparableValue(normalizedQuery);
    if (compactQuery && compactQuery !== normalizedQuery.toLocaleLowerCase('tr')) {
      queries.push(compactQuery);
    }

    queries.push(`sku:"${normalizedQuery}"`);

    const withoutPrefix = normalizedQuery.replace(/^KRC-/i, '');
    if (withoutPrefix && withoutPrefix !== normalizedQuery) {
      queries.push(`sku:"${withoutPrefix}"`);
    }
  }

  return [...new Set(queries.filter(Boolean))];
}

function getProductMatchScore(product, query) {
  const rawQuery = normalizeSearchTerm(query).toLocaleLowerCase('tr');
  const normalizedQuery = normalizeComparableValue(query);
  const title = (product.title || '').toLocaleLowerCase('tr');
  const sku = (product.sku || '').toLocaleLowerCase('tr');
  const handle = (product.handle || '').toLocaleLowerCase('tr');
  const normalizedTitle = normalizeComparableValue(product.title || '');
  const normalizedSku = normalizeComparableValue(product.sku || '');
  const normalizedHandle = normalizeComparableValue(product.handle || '');

  if (!rawQuery) return 0;
  if (sku === rawQuery) return 120;
  if (normalizedSku && normalizedSku === normalizedQuery) return 110;
  if (title === rawQuery) return 100;
  if (handle === rawQuery) return 95;
  if (sku.startsWith(rawQuery)) return 90;
  if (normalizedSku.startsWith(normalizedQuery)) return 88;
  if (title.startsWith(rawQuery)) return 80;
  if (normalizedTitle.startsWith(normalizedQuery)) return 76;
  if (handle.startsWith(rawQuery)) return 72;
  if (sku.includes(rawQuery)) return 66;
  if (normalizedSku.includes(normalizedQuery)) return 64;
  if (title.includes(rawQuery)) return 56;
  if (normalizedTitle.includes(normalizedQuery)) return 52;
  if (handle.includes(rawQuery) || normalizedHandle.includes(normalizedQuery)) return 46;

  return 0;
}

function escapeAccessoryFinderHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function extractHandleFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    const parts = parsed.pathname.split('/').filter(Boolean);
    const productIndex = parts.indexOf('products');

    if (productIndex >= 0 && parts[productIndex + 1]) {
      return parts[productIndex + 1];
    }
  } catch (error) {
    return '';
  }

  return '';
}

class AccessoryFinder extends HTMLElement {
  constructor() {
    super();

    this.categoryCache = new Map();
    this.activeCategory = null;
    this.selectedMachine = null;
    this.categorySuggestions = [];
    this.searchSuggestions = [];
    this.searchRequestId = 0;
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundPopState = this.handlePopState.bind(this);
    this.debouncedCategoryInput = accessoryFinderDebounce(this.handleCategoryInput.bind(this), 180);
    this.debouncedSearchInput = accessoryFinderDebounce(this.handleSearchInput.bind(this), 220);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cacheDom();
    this.bindEvents();
    this.initializeFromUrl();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundDocumentClick);
    window.removeEventListener('popstate', this.boundPopState);
  }

  cacheDom() {
    this.categoryButtons = Array.from(this.querySelectorAll('[data-category-button]'));
    this.categoryTabButtons = Array.from(this.querySelectorAll('[data-category-tab-button]'));
    this.categoryTabPanes = Array.from(this.querySelectorAll('[data-category-tab-pane]'));
    this.categoryInput = this.querySelector('[data-category-input]');
    this.categoryDropdown = this.querySelector('[data-category-dropdown]');
    this.categorySuggestionsList = this.querySelector('[data-category-suggestions]');
    this.categoryStatus = this.querySelector('[data-category-status]');
    this.clearCategoryInputButton = this.querySelector('[data-clear-category-input]');

    this.searchInput = this.querySelector('[data-search-input]');
    this.searchDropdown = this.querySelector('[data-search-dropdown]');
    this.searchSuggestionsList = this.querySelector('[data-search-suggestions]');
    this.searchStatus = this.querySelector('[data-search-status]');
    this.clearSearchInputButton = this.querySelector('[data-clear-search-input]');
    this.searchExampleButtons = Array.from(this.querySelectorAll('[data-search-example]'));

    this.selectionSummary = this.querySelector('[data-selection-summary]');
    this.selectionLabel = this.querySelector('[data-selection-label]');
    this.selectionTitle = this.querySelector('[data-selection-title]');
    this.selectionMeta = this.querySelector('[data-selection-meta]');
    this.selectionLink = this.querySelector('[data-selection-link]');
    this.clearSelectionButton = this.querySelector('[data-clear-selection]');
    this.resultsColumn = this.querySelector('[data-results-column]');

    this.idleState = this.querySelector('[data-idle-state]');
    this.loadingState = this.querySelector('[data-loading-state]');
    this.errorState = this.querySelector('[data-error-state]');
    this.resultsRegion = this.querySelector('[data-results-region]');
  }

  bindEvents() {
    this.categoryTabButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.activateCategoryTab(button.dataset.categoryTabTarget);
      });
    });

    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const category = {
          handle: button.dataset.categoryHandle,
          title: button.dataset.categoryTitle,
        };

        if (button.dataset.categoryGroup) {
          this.activateCategoryTab(button.dataset.categoryGroup, { skipFocus: true });
        }

        if (this.activeCategory?.handle === category.handle) {
          this.deactivateCategory();
          return;
        }

        this.activateCategory(category);
      });
    });

    this.categoryInput?.addEventListener('input', (event) => {
      this.toggleInputAction(this.clearCategoryInputButton, event.target.value);
      if (this.selectedMachine?.source === 'category') {
        this.clearMachineSelection({ preserveInputs: true, skipSync: true });
      }
      this.debouncedCategoryInput();
    });

    this.categoryInput?.addEventListener('keydown', (event) => this.handleInputKeydown(event, 'category'));
    this.categoryInput?.addEventListener('focus', () => {
      if (!this.activeCategory) return;
      this.renderCategorySuggestions(this.categorySuggestions.length ? this.categorySuggestions : this.getDefaultCategorySuggestions());
      this.openDropdown('category');
    });
    this.categorySuggestionsList?.addEventListener('click', (event) => this.handleSuggestionClick(event, 'category'));

    this.searchInput?.addEventListener('input', (event) => {
      this.toggleInputAction(this.clearSearchInputButton, event.target.value);
      if (this.selectedMachine?.source === 'search') {
        this.clearMachineSelection({ preserveInputs: true, skipSync: true });
      }
      this.debouncedSearchInput();
    });

    this.searchInput?.addEventListener('keydown', (event) => this.handleInputKeydown(event, 'search'));
    this.searchInput?.addEventListener('focus', () => {
      if (this.searchSuggestions.length) {
        this.openDropdown('search');
      } else if (normalizeSearchTerm(this.searchInput.value)) {
        this.handleSearchInput();
      }
    });
    this.searchSuggestionsList?.addEventListener('click', (event) => this.handleSuggestionClick(event, 'search'));
    this.searchExampleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = normalizeSearchTerm(button.dataset.searchExample || '');
        if (!value) return;
        this.searchInput.value = value;
        this.toggleInputAction(this.clearSearchInputButton, value);
        this.searchInput.focus();
        this.handleSearchInput();
      });
    });

    this.clearCategoryInputButton?.addEventListener('click', () => {
      if (this.selectedMachine?.source === 'category') {
        this.clearMachineSelection({ preserveInputs: true, skipSync: true });
      }
      this.categoryInput.value = '';
      this.categoryInput.focus();
      this.toggleInputAction(this.clearCategoryInputButton, '');
      this.renderCategorySuggestions(this.getDefaultCategorySuggestions());
      this.setCategoryStatus(
        this.activeCategory
          ? 'Ürün adını ya da model kodunu yazarak seçiminizi daraltın.'
          : 'Önce yukarıdan bir kategori seçin.'
      );
      if (this.activeCategory) {
        this.openDropdown('category');
      }
      this.syncUrl();
    });

    this.clearSearchInputButton?.addEventListener('click', () => {
      if (this.selectedMachine?.source === 'search') {
        this.clearMachineSelection({ preserveInputs: true, skipSync: true });
      }
      this.searchInput.value = '';
      this.searchInput.focus();
      this.toggleInputAction(this.clearSearchInputButton, '');
      this.renderSearchSuggestions([]);
      this.setSearchStatus(this.getDefaultSearchStatus());
      this.syncUrl();
    });

    this.clearSelectionButton?.addEventListener('click', () => {
      this.resetSelection();
    });

    document.addEventListener('click', this.boundDocumentClick);
    window.addEventListener('popstate', this.boundPopState);
  }

  async initializeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const categoryHandle = params.get('category');
    const machineHandle = params.get('machine');
    const query = normalizeSearchTerm(params.get('q') || '');

    this.clearMachineSelection({ preserveInputs: false, skipSync: true });
    this.renderCategorySuggestions([]);
    this.renderSearchSuggestions([]);

    if (query) {
      this.searchInput.value = query;
      this.toggleInputAction(this.clearSearchInputButton, query);
    }

    if (categoryHandle) {
      const matchingButton = this.categoryButtons.find((button) => button.dataset.categoryHandle === categoryHandle);
      const categoryTitle = matchingButton?.dataset.categoryTitle || categoryHandle;

       if (matchingButton?.dataset.categoryGroup) {
        this.activateCategoryTab(matchingButton.dataset.categoryGroup, { skipFocus: true });
      }

      await this.activateCategory(
        {
          handle: categoryHandle,
          title: categoryTitle,
        },
        {
          preserveMachine: true,
          skipSync: true,
          fromUrl: true,
        }
      );
    } else {
      const defaultTab = this.categoryTabButtons.find((button) => button.classList.contains('is-active'));
      if (defaultTab) {
        this.activateCategoryTab(defaultTab.dataset.categoryTabTarget, { skipFocus: true });
      }
      this.deactivateCategory({ preserveSearch: true, preserveMachine: true, skipSync: true });
    }

    if (machineHandle) {
      const machine = this.getCachedCategoryProduct(machineHandle) || {
        handle: machineHandle,
        title: '',
        sku: '',
        url: `/products/${machineHandle}`,
      };

      await this.selectMachine(machine, {
        source: categoryHandle ? 'category' : query ? 'search' : 'url',
        skipSync: true,
        clearCategory: false,
      });
    } else if (query) {
      await this.handleSearchInput();
    }

    this.focusSearchInput();
  }

  focusSearchInput() {
    if (!this.searchInput) return;

    window.requestAnimationFrame(() => {
      this.searchInput.focus({ preventScroll: true });
      this.searchInput.setSelectionRange(this.searchInput.value.length, this.searchInput.value.length);
    });
  }

  async activateCategory(category, options = {}) {
    const { preserveMachine = false, skipSync = false, fromUrl = false } = options;

    this.activeCategory = category;
    const matchingButton = this.categoryButtons.find((button) => button.dataset.categoryHandle === category.handle);
    if (matchingButton?.dataset.categoryGroup) {
      this.activateCategoryTab(matchingButton.dataset.categoryGroup, { skipFocus: true });
    }
    this.categoryButtons.forEach((button) => {
      const isActive = button.dataset.categoryHandle === category.handle;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.classList.toggle('is-active', isActive);
    });

    this.categoryInput.disabled = false;
    this.categoryInput.value = fromUrl && preserveMachine ? this.categoryInput.value : '';
    this.toggleInputAction(this.clearCategoryInputButton, this.categoryInput.value);
    this.closeDropdown('search');

    if (!preserveMachine) {
      this.searchInput.value = '';
      this.toggleInputAction(this.clearSearchInputButton, '');
      this.clearMachineSelection({ preserveInputs: true, skipSync: true });
    }

    this.setCategoryStatus('Kategori ürünleri hazırlanıyor...');

    try {
      await this.ensureCategoryPage(category.handle, 1);
      this.setCategoryStatus('Ürün adını ya da model kodunu yazarak seçiminizi daraltın.');
      this.renderCategorySuggestions(this.getDefaultCategorySuggestions());
      this.openDropdown('category');
    } catch (error) {
      this.setCategoryStatus('Kategori ürünleri yüklenemedi. Lütfen tekrar deneyin.');
    }

    if (!skipSync) this.syncUrl();
  }

  deactivateCategory(options = {}) {
    const { preserveSearch = false, preserveMachine = false, skipSync = false } = options;

    this.activeCategory = null;
    this.categoryButtons.forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.classList.remove('is-active');
    });

    this.categoryInput.value = '';
    this.categoryInput.disabled = true;
    this.toggleInputAction(this.clearCategoryInputButton, '');
    this.renderCategorySuggestions([]);
    this.closeDropdown('category');
    this.setCategoryStatus('Önce yukarıdan bir kategori seçin.');

    if (!preserveMachine) {
      this.clearMachineSelection({ preserveInputs: true, skipSync: true });
    }

    if (!preserveSearch) {
      this.searchInput.value = '';
      this.toggleInputAction(this.clearSearchInputButton, '');
      this.renderSearchSuggestions([]);
      this.setSearchStatus(this.getDefaultSearchStatus());
    }

    if (!skipSync) this.syncUrl();
  }

  async ensureCategoryPage(handle, page) {
    let cache = this.categoryCache.get(handle);

    if (!cache) {
      cache = {
        title: handle,
        products: [],
        pagesLoaded: new Set(),
        totalPages: null,
      };
      this.categoryCache.set(handle, cache);
    }

    if (cache.pagesLoaded.has(page)) return cache;

    const response = await fetch(
      `/collections/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.dataset.optionsSection)}&page=${page}`
    );

    if (!response.ok) {
      throw new Error(`Category options fetch failed: ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const payloadEl = doc.querySelector('[data-accessory-finder-options]');

    if (!payloadEl) {
      throw new Error('Missing category payload');
    }

    const payload = JSON.parse(payloadEl.textContent);
    cache.title = payload.collection?.title || cache.title;
    cache.totalPages = payload.pagination?.pages || 1;
    cache.pagesLoaded.add(payload.pagination?.current_page || page);

    const knownHandles = new Set(cache.products.map((product) => product.handle));
    (payload.products || []).forEach((product) => {
      if (!knownHandles.has(product.handle)) {
        cache.products.push(product);
        knownHandles.add(product.handle);
      }
    });

    return cache;
  }

  async handleCategoryInput() {
    if (!this.activeCategory) return;

    const query = normalizeSearchTerm(this.categoryInput.value);
    const matches = await this.getCategoryMatches(query);

    this.renderCategorySuggestions(matches, query);

    if (!query) {
      this.setCategoryStatus('Ürün adını ya da model kodunu yazarak seçiminizi daraltın.');
    } else if (matches.length) {
      this.setCategoryStatus(`${matches.length} eşleşme gösteriliyor.`);
    } else {
      this.setCategoryStatus('Bu kategori altında eşleşen ürün bulunamadı.');
    }

    this.syncUrl();
  }

  async getCategoryMatches(query) {
    const cache = this.categoryCache.get(this.activeCategory?.handle);
    if (!cache) return [];

    const normalizedQuery = normalizeSearchTerm(query).toLocaleLowerCase('tr');
    let matches = this.filterCategoryProducts(cache.products, normalizedQuery);

    while (normalizedQuery && matches.length < 8 && cache.totalPages && cache.pagesLoaded.size < cache.totalPages) {
      const nextPage = cache.pagesLoaded.size + 1;
      await this.ensureCategoryPage(this.activeCategory.handle, nextPage);
      matches = this.filterCategoryProducts(cache.products, normalizedQuery);
    }

    return normalizedQuery ? matches.slice(0, 8) : cache.products.slice(0, 8);
  }

  filterCategoryProducts(products, normalizedQuery) {
    if (!normalizedQuery) return products.slice(0, 8);

    const comparableQuery = normalizeComparableValue(normalizedQuery);

    return products.filter((product) => {
      const haystacks = [product.title, product.sku, product.handle]
        .filter(Boolean)
        .map((value) => value.toLocaleLowerCase('tr'));

      const comparableHaystacks = [product.title, product.sku, product.handle]
        .filter(Boolean)
        .map((value) => normalizeComparableValue(value));

      return haystacks.some((value) => value.includes(normalizedQuery))
        || comparableHaystacks.some((value) => comparableQuery && value.includes(comparableQuery));
    });
  }

  getDefaultCategorySuggestions() {
    const cache = this.categoryCache.get(this.activeCategory?.handle);
    return cache ? cache.products.slice(0, 8) : [];
  }

  async handleSearchInput() {
    const query = normalizeSearchTerm(this.searchInput.value);
    if (!query) {
      this.renderSearchSuggestions([]);
      this.setSearchStatus(this.getDefaultSearchStatus());
      this.syncUrl();
      return;
    }

    const requestId = ++this.searchRequestId;
    this.setSearchStatus('Aranıyor...');

    try {
      const results = await this.fetchPredictiveProducts(query);
      if (requestId !== this.searchRequestId) return;

      this.renderSearchSuggestions(results, query);

      if (results.length) {
        this.setSearchStatus(`${results.length} eşleşme bulundu.`);
      } else {
        this.setSearchStatus('Eşleşme bulunamadı. Farklı bir ürün adı, kodu veya aksesuar numarası deneyin.');
      }
    } catch (error) {
      if (requestId !== this.searchRequestId) return;
      this.renderSearchSuggestions([]);
      this.setSearchStatus('Arama sonuçları getirilemedi.');
    }

    this.syncUrl();
  }

  async fetchPredictiveProducts(query) {
    const productMap = new Map();
    const queries = buildPredictiveSearchQueries(query);

    for (const searchQuery of queries) {
      const url = new URL(this.dataset.predictiveSearchUrl, window.location.origin);
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');

      const response = await fetch(url.toString());
      if (!response.ok) continue;

      const payload = await response.json();
      const products = payload?.resources?.results?.products || [];

      products.forEach((product) => {
        const handle = extractHandleFromUrl(product.url);
        if (!handle || productMap.has(handle)) return;

        productMap.set(handle, {
          id: product.id,
          handle,
          title: product.title,
          url: product.url,
          sku: product.variants?.[0]?.sku || '',
          image: product.featured_image?.url || product.image || '',
          productType: product.product_type || '',
        });
      });
    }

    return Array.from(productMap.values())
      .sort((left, right) => getProductMatchScore(right, query) - getProductMatchScore(left, query))
      .slice(0, 8);
  }

  handleInputKeydown(event, kind) {
    const list = kind === 'category' ? this.categorySuggestionsList : this.searchSuggestionsList;
    if (!list) return;

    if (event.key === 'ArrowDown') {
      const firstButton = list.querySelector('button');
      if (firstButton) {
        event.preventDefault();
        firstButton.focus();
      }
    }

    if (event.key === 'Enter') {
      const firstButton = list.querySelector('button');
      if (firstButton && !this.getDropdown(kind).hidden) {
        event.preventDefault();
        firstButton.click();
      }
    }

    if (event.key === 'Escape') {
      this.closeDropdown(kind);
    }
  }

  handleSuggestionClick(event, kind) {
    const button = event.target.closest('[data-product-handle]');
    if (!button) return;

    const machine = {
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      sku: button.dataset.productSku || '',
      url: button.dataset.productUrl,
    };

    if (kind === 'category') {
      this.selectMachine(machine, { source: 'category', clearCategory: false });
    } else {
      this.selectMachine(machine, { source: 'search', clearCategory: true });
    }
  }

  async selectMachine(machine, options = {}) {
    const { source = 'manual', skipSync = false, clearCategory = false } = options;

    if (clearCategory && this.activeCategory) {
      this.deactivateCategory({ preserveSearch: true, preserveMachine: true, skipSync: true });
    }

    this.selectedMachine = {
      ...machine,
      source,
    };

    if (source === 'category') {
      this.categoryInput.value = machine.title || machine.handle;
      this.toggleInputAction(this.clearCategoryInputButton, this.categoryInput.value);
      this.searchInput.value = '';
      this.toggleInputAction(this.clearSearchInputButton, '');
      this.renderSearchSuggestions([]);
      this.setSearchStatus(this.getDefaultSearchStatus());
    }

    this.renderSelectionSummary();
    this.closeDropdown('category');
    this.closeDropdown('search');
    await this.loadResults(machine);

    if (!skipSync) this.syncUrl();
  }

  async loadResults(machine) {
    this.showState('loading');

    try {
      const response = await fetch(
        `${machine.url}${machine.url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(this.dataset.resultsSection)}`
      );

      if (!response.ok) {
        throw new Error(`Results fetch failed: ${response.status}`);
      }

      const html = await response.text();
      this.resultsRegion.innerHTML = html;
      this.resultsRegion.classList.remove('hidden');

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const payload = doc.querySelector('[data-accessory-finder-results]');

      if (payload) {
        const machineTitle = payload.dataset.machineTitle || machine.title || machine.handle;
        const machineSku = payload.dataset.machineSku || machine.sku || '';
        const machineUrl = payload.dataset.machineUrl || machine.url;
        const accessoryCount = Number(payload.dataset.accessoryCount || 0);
        const selectionLabel = payload.dataset.selectionLabel || 'Seçili ürün';

        this.selectedMachine = {
          ...this.selectedMachine,
          title: machineTitle,
          sku: machineSku,
          url: machineUrl,
          selectionLabel,
        };

        if (this.activeCategory) {
          this.categoryInput.value = machineTitle;
          this.toggleInputAction(this.clearCategoryInputButton, machineTitle);
        }

        if (accessoryCount === 0) {
          const emptyTitle = this.resultsRegion.querySelector('.accessory-finder-results__empty h3');
          const emptyText = this.resultsRegion.querySelector('.accessory-finder-results__empty p');
          if (emptyTitle) emptyTitle.textContent = this.dataset.noResultsTitle;
          if (emptyText) emptyText.textContent = this.dataset.noResultsText;
        }
      }

      this.renderSelectionSummary();
      this.showState('results');
    } catch (error) {
      this.showState('error');
    }
  }

  clearMachineSelection(options = {}) {
    const { preserveInputs = true, skipSync = false } = options;

    this.selectedMachine = null;
    if (this.resultsColumn) this.resultsColumn.dataset.selectionState = 'empty';
    this.selectionSummary.hidden = true;
    this.selectionTitle.textContent = '';
    this.selectionLink.hidden = true;
    this.selectionLink.removeAttribute('href');
    this.selectionMeta.hidden = true;
    this.selectionMeta.textContent = '';
    this.resultsRegion.innerHTML = '';
    this.resultsRegion.classList.add('hidden');
    this.showState('idle');

    if (!preserveInputs) {
      this.categoryInput.value = '';
      this.searchInput.value = '';
      this.toggleInputAction(this.clearCategoryInputButton, '');
      this.toggleInputAction(this.clearSearchInputButton, '');
    }

    if (!skipSync) this.syncUrl();
  }

  resetSelection() {
    const selectedSource = this.selectedMachine?.source;
    this.clearMachineSelection({ preserveInputs: true, skipSync: true });

    if (selectedSource === 'category' && this.activeCategory) {
      this.categoryInput.value = '';
      this.toggleInputAction(this.clearCategoryInputButton, '');
      this.renderCategorySuggestions(this.getDefaultCategorySuggestions());
      this.openDropdown('category');
      this.categoryInput.focus();
      this.setCategoryStatus('Farklı bir ürün seçin.');
    } else {
      this.searchInput.focus();
      if (normalizeSearchTerm(this.searchInput.value)) {
        this.handleSearchInput();
        this.openDropdown('search');
        this.setSearchStatus('Farklı bir ürün seçin.');
      } else {
        this.searchInput.value = '';
        this.toggleInputAction(this.clearSearchInputButton, '');
        this.renderSearchSuggestions([]);
        this.setSearchStatus(this.getDefaultSearchStatus());
      }
    }

    this.syncUrl();
  }

  renderSelectionSummary() {
    if (!this.selectedMachine) {
      if (this.resultsColumn) this.resultsColumn.dataset.selectionState = 'empty';
      this.selectionSummary.hidden = true;
      if (this.selectionLabel) this.selectionLabel.textContent = 'Seçili ürün';
      return;
    }

    const title = this.selectedMachine.title || this.selectedMachine.handle;
    const metaParts = [];
    if (this.selectionLabel) {
      this.selectionLabel.textContent = this.selectedMachine.selectionLabel || 'Seçili ürün';
    }
    if (this.activeCategory?.title) metaParts.push(this.activeCategory.title);
    if (this.selectedMachine.sku) metaParts.push(`Kod: ${this.selectedMachine.sku}`);

    this.selectionTitle.textContent = title;
    if (metaParts.length) {
      this.selectionMeta.textContent = metaParts.join(' • ');
      this.selectionMeta.hidden = false;
    } else {
      this.selectionMeta.hidden = true;
      this.selectionMeta.textContent = '';
    }

    if (this.selectedMachine.url) {
      this.selectionLink.href = this.selectedMachine.url;
      this.selectionLink.hidden = false;
    } else {
      this.selectionLink.hidden = true;
    }

    if (this.resultsColumn) this.resultsColumn.dataset.selectionState = 'active';
    this.selectionSummary.hidden = false;
  }

  renderCategorySuggestions(products, query = '') {
    this.categorySuggestions = products;
    this.renderSuggestionList(this.categorySuggestionsList, products, {
      emptyTitle: query ? 'Bu kategori altında eşleşen ürün bulunamadı.' : '',
      kind: 'category',
    });
  }

  renderSearchSuggestions(products, query = '') {
    this.searchSuggestions = products;
    this.renderSuggestionList(this.searchSuggestionsList, products, {
      emptyTitle: query ? 'Bu aramayla eşleşen ürün bulunamadı.' : '',
      kind: 'search',
    });
  }

  renderSuggestionList(target, products, options = {}) {
    const { emptyTitle = '', kind } = options;
    if (!target) return;

    if (!products.length) {
      target.innerHTML = emptyTitle
        ? `<li class="accessory-finder__suggestion-empty">${escapeAccessoryFinderHtml(emptyTitle)}</li>`
        : '';
      this.getDropdown(kind).hidden = !emptyTitle;
      this.getInput(kind)?.setAttribute('aria-expanded', emptyTitle ? 'true' : 'false');
      return;
    }

    target.innerHTML = products
      .map((product) => {
        const imageMarkup = product.image
          ? `<img src="${escapeAccessoryFinderHtml(product.image)}" alt="${escapeAccessoryFinderHtml(
              product.title
            )}" loading="lazy">`
          : `<span class="accessory-finder__suggestion-placeholder" aria-hidden="true">${escapeAccessoryFinderHtml(
              product.title.slice(0, 1)
            )}</span>`;

        const metaParts = [product.sku, product.productType].filter(Boolean);

        return `
          <li>
            <button
              type="button"
              class="accessory-finder__suggestion"
              data-product-handle="${escapeAccessoryFinderHtml(product.handle)}"
              data-product-title="${escapeAccessoryFinderHtml(product.title)}"
              data-product-sku="${escapeAccessoryFinderHtml(product.sku || '')}"
              data-product-url="${escapeAccessoryFinderHtml(product.url)}"
            >
              <span class="accessory-finder__suggestion-media">${imageMarkup}</span>
              <span class="accessory-finder__suggestion-copy">
                <strong>${escapeAccessoryFinderHtml(product.title)}</strong>
                ${metaParts.length ? `<span>${escapeAccessoryFinderHtml(metaParts.join(' • '))}</span>` : ''}
              </span>
            </button>
          </li>
        `;
      })
      .join('');

    this.getDropdown(kind).hidden = false;
    this.getInput(kind)?.setAttribute('aria-expanded', 'true');
  }

  getCachedCategoryProduct(handle) {
    if (!this.activeCategory) return null;
    const cache = this.categoryCache.get(this.activeCategory.handle);
    if (!cache) return null;
    return cache.products.find((product) => product.handle === handle) || null;
  }

  showState(state) {
    this.idleState.classList.toggle('hidden', state !== 'idle');
    this.loadingState.classList.toggle('hidden', state !== 'loading');
    this.errorState.classList.toggle('hidden', state !== 'error');
    this.resultsRegion.classList.toggle('hidden', state !== 'results');
  }

  setCategoryStatus(message) {
    if (this.categoryStatus) this.categoryStatus.textContent = message;
  }

  setSearchStatus(message) {
    if (this.searchStatus) this.searchStatus.textContent = message;
  }

  getDefaultSearchStatus() {
    return 'Örnek: SC 3, Puzzi 10/1, 2.863-259.0 veya 6.904-285.0';
  }

  activateCategoryTab(groupKey, options = {}) {
    const { skipFocus = false } = options;
    if (!groupKey || !this.categoryTabButtons.length || !this.categoryTabPanes.length) return;

    this.categoryTabButtons.forEach((button) => {
      const isActive = button.dataset.categoryTabTarget === groupKey;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive && !skipFocus) {
        button.focus({ preventScroll: true });
      }
    });

    this.categoryTabPanes.forEach((pane) => {
      pane.classList.toggle('hidden', pane.dataset.categoryTabPane !== groupKey);
    });
  }

  getDropdown(kind) {
    return kind === 'category' ? this.categoryDropdown : this.searchDropdown;
  }

  getInput(kind) {
    return kind === 'category' ? this.categoryInput : this.searchInput;
  }

  closeDropdown(kind) {
    const dropdown = this.getDropdown(kind);
    const input = this.getInput(kind);
    if (!dropdown || !input) return;
    dropdown.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  }

  openDropdown(kind) {
    const dropdown = this.getDropdown(kind);
    const input = this.getInput(kind);
    if (!dropdown || !input) return;
    dropdown.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeDropdown('category');
      this.closeDropdown('search');
    }
  }

  handlePopState() {
    this.initializeFromUrl();
  }

  toggleInputAction(button, value) {
    if (!button) return;
    button.hidden = !String(value || '').trim();
  }

  syncUrl() {
    const params = new URLSearchParams();

    if (this.activeCategory?.handle) {
      params.set('category', this.activeCategory.handle);
    }

    if (this.selectedMachine?.handle) {
      params.set('machine', this.selectedMachine.handle);
    }

    const query = normalizeSearchTerm(this.searchInput?.value || '');
    if (query) {
      params.set('q', query);
    }

    const url = new URL(window.location.href);
    const nextSearch = params.toString();
    url.search = nextSearch;

    window.history.replaceState({ search: nextSearch }, '', url.toString());
  }
}

if (!customElements.get('accessory-finder')) {
  customElements.define('accessory-finder', AccessoryFinder);
}
